export type RepescagemStage =
  | 'LEAD'
  | 'TENTATIVA'
  | 'SEM_RETORNO'
  | 'DECLINADO'
  | 'INTERESSADO';

export interface RepescagemLead {
  id: string;
  quoteNumber: number;
  clientName: string;
  clientEmail: string | null;
  sellerName: string | null;
  quoteDate: string; // "2026-02-11"
  quoteStatus: string; // "ORCAMENTO" | "ORCAMENTO_RECUSADO"
  quoteStatusLabel: string; // "Em andamento" | "Recusado"
  totalValue: number | null;
  stage: RepescagemStage;
  attemptCount: number;
  maxAttempts: number;
  lastAttemptAt: string | null;
  claimedBy: string | null;
  contactNames: string[];
  contactEmails: string[];
  contactPhones: string[];
}

export interface RepescagemAttempt {
  id: string;
  attemptNumber: number;
  attemptedAt: string;
  channel: string; // TELEFONE | WHATSAPP | EMAIL
  outcome: string; // NAO_ATENDEU | PEDIU_RETORNO | CONTATO_REALIZADO | CAIXA_POSTAL | NUMERO_INVALIDO | EMAIL_ENVIADO
  notes: string | null;
  sdrName: string | null;
}

export interface RepescagemLeadItem {
  name: string;
  description: string | null;
  type: string;
  quantity: number;
  unitPrice: number;
}

export interface RepescagemLeadDetail extends RepescagemLead {
  contactRaw: string | null;
  generalNotes: string | null;
  paymentObservations: string | null;
  items: RepescagemLeadItem[];
  attempts: RepescagemAttempt[];
  reengagementCount: number;
  reengagementAt: string | null;
  cooldownUntil: string | null;
}

export interface KanbanColumns {
  LEAD: RepescagemLead[];
  TENTATIVA: RepescagemLead[];
  SEM_RETORNO: RepescagemLead[];
  DECLINADO: RepescagemLead[];
  INTERESSADO: RepescagemLead[];
}

export interface KanbanCounts {
  LEAD: number;
  TENTATIVA: number;
  SEM_RETORNO: number;
  DECLINADO: number;
  INTERESSADO: number;
}

export interface KanbanResponse {
  columns: KanbanColumns;
  counts: KanbanCounts;
}

export interface SyncStatus {
  status: string;
  lastSyncAt: string | null;
  imported: number;
  skipped: number;
  errors: number;
  message: string | null;
  totalToProcess?: number;
}

export interface RepescagemStats {
  LEAD: number;
  TENTATIVA: number;
  SEM_RETORNO: number;
  DECLINADO: number;
  INTERESSADO: number;
  total: number;
}

export interface StageConfig {
  label: string;
  color: string;
  headerColor: string;
  badgeColor: string;
}

export const STAGE_CONFIG: Record<RepescagemStage, StageConfig> = {
  LEAD: {
    label: 'Lead',
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    headerColor: 'border-t-blue-500',
    badgeColor: 'bg-blue-500/10 text-blue-500',
  },
  TENTATIVA: {
    label: 'Tentativa',
    color: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    headerColor: 'border-t-amber-500',
    badgeColor: 'bg-amber-500/10 text-amber-500',
  },
  SEM_RETORNO: {
    label: 'Sem Retorno',
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    headerColor: 'border-t-gray-500',
    badgeColor: 'bg-gray-500/10 text-gray-500',
  },
  DECLINADO: {
    label: 'Declinado',
    color: 'bg-red-500/10 text-red-500 border-red-500/20',
    headerColor: 'border-t-red-500',
    badgeColor: 'bg-red-500/10 text-red-500',
  },
  INTERESSADO: {
    label: 'Interessado',
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    headerColor: 'border-t-green-500',
    badgeColor: 'bg-green-500/10 text-green-500',
  },
};

export const STAGE_ORDER: RepescagemStage[] = [
  'LEAD',
  'TENTATIVA',
  'SEM_RETORNO',
  'DECLINADO',
  'INTERESSADO',
];

export const CHANNEL_LABELS: Record<string, string> = {
  TELEFONE: 'Telefone',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'E-mail',
};

export const OUTCOME_LABELS: Record<string, string> = {
  NAO_ATENDEU: 'Não atendeu',
  PEDIU_RETORNO: 'Pediu retorno',
  CONTATO_REALIZADO: 'Contato realizado',
  CAIXA_POSTAL: 'Caixa postal',
  NUMERO_INVALIDO: 'Número inválido',
  EMAIL_ENVIADO: 'E-mail enviado',
};

export function formatCurrency(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  // Append T12:00:00 to date-only strings to avoid UTC midnight → previous day in Brazil (UTC-3)
  const safe = dateStr.length === 10 ? dateStr + 'T12:00:00' : dateStr;
  return new Date(safe).toLocaleDateString('pt-BR');
}

export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
